/* Original variant IDs, one multi-item Shopify cart request, no fulfilment changes. */
(() => {
  'use strict';
  if (customElements.get('sm-complete-look')) return;

  // Product JSON may contain the original upload. Resize only recognised Shopify
  // image routes; retain version/crop parameters and other image hosts unchanged.
  const thumbnailURL = (source, width) => {
    if (typeof source !== 'string' || !source) return source;
    try {
      const url = new URL(source, window.location.origin);
      const shopifyCDN = url.hostname === 'cdn.shopify.com' && /^\/s\/files\//.test(url.pathname);
      const storefrontCDN = url.origin === window.location.origin && /^\/cdn\/shop\/(?:files|products)\//.test(url.pathname);
      if (!/^https?:$/.test(url.protocol) || (!shopifyCDN && !storefrontCDN)) return source;
      const currentWidth = Number(url.searchParams.get('width'));
      if (currentWidth > 0 && currentWidth <= width) return source;
      url.searchParams.set('width', String(width));
      return url.href;
    } catch (_) { return source; }
  };

  class SMCompleteLook extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.busy = false;
      this.uncertain = false;
      this.form = this.querySelector('[data-sm-look-form]');
      this.button = this.querySelector('[data-sm-look-add]');
      this.status = this.querySelector('[data-sm-look-status]');
      this.total = this.querySelector('[data-sm-look-total]');
      this.cartLink = this.querySelector('[data-sm-look-cart]');
      if (!this.form || !this.button) return;
      try {
        this.pieces = Array.from(this.querySelectorAll('sm-look-piece')).map(node => ({
          node,
          data: JSON.parse(node.querySelector('[data-sm-piece-data]').textContent),
          selects: Array.from(node.querySelectorAll('[data-sm-option]')),
          include: node.querySelector('[data-sm-piece-include]'),
          variant: null
        }));
        this.money = new Intl.NumberFormat(this.dataset.locale || document.documentElement.lang || 'en-GB', {
          style: 'currency', currency: this.dataset.currency || 'GBP'
        });
        this.form.addEventListener('change', () => this.update());
        this.form.addEventListener('submit', event => this.add(event));
        this.update();
      } catch (_) {
        this.status.textContent = 'Please open each piece to choose your options.';
        this.button.disabled = true;
      }
    }

    update() {
      if (this.busy || this.uncertain) return;
      let total = 0;
      let allSelected = true;
      let allAvailable = true;
      let includedCount = 0;
      for (const piece of this.pieces) {
        piece.included = !piece.include || piece.include.checked;
        piece.node.toggleAttribute('data-sm-piece-excluded', !piece.included);
        piece.selects.forEach(select => { select.disabled = !piece.included; });
        const values = piece.selects.map(select => select.value);
        const complete = values.every(value => value !== '');
        piece.variant = complete ? piece.data.variants.find(variant => !values.length || values.every((value,index) => variant.options[Number(piece.selects[index].dataset.optionIndex ?? index)] === value)) : null;
        if (piece.included) {
          includedCount += 1;
          allSelected = allSelected && complete;
          allAvailable = allAvailable && Boolean(piece.variant && piece.variant.available && !piece.data.requiresSellingPlan);
        }
        const message = piece.node.querySelector('[data-sm-piece-availability]');
        message.textContent = !piece.included
          ? `Not included.${piece.data.variants.some(variant => variant.available) ? '' : ' Currently unavailable.'}`
          : piece.data.requiresSellingPlan ? 'Open this piece to choose its purchase plan, or remove it from this selection.'
          : complete && !piece.variant ? 'This combination is unavailable. Please choose another option.'
          : piece.variant && !piece.variant.available ? 'This selection is currently unavailable.' : '';
        const productURL = new URL(piece.node.dataset.productUrl, window.location.origin);
        const fitLink = piece.node.querySelector('[data-sm-piece-fit]');
        const fitURL = new URL(fitLink.href, window.location.origin);
        fitURL.searchParams.delete('sm_variant');
        if (piece.variant) {
          if (piece.included) total += Number(piece.variant.price);
          piece.node.querySelector('[data-sm-piece-price]').textContent = piece.variant.formattedPrice;
          productURL.searchParams.set('variant', String(piece.variant.id));
          fitURL.searchParams.set('sm_variant', String(piece.variant.id));
          if (piece.variant.image) {
            const img = piece.node.querySelector('.sm-look-item__image');
            if (img) {
              img.removeAttribute('srcset');
              img.src = thumbnailURL(piece.variant.image, img.closest('.sm-ctl-purchase') ? 224 : 320);
            }
          }
        } else {
          const available = piece.data.variants.filter(v => v.available);
          const choices = available.length ? available : piece.data.variants;
          const cheapest = choices.reduce((best,v) => !best || v.price < best.price ? v : best,null);
          if (cheapest) piece.node.querySelector('[data-sm-piece-price]').textContent = cheapest.formattedPrice;
        }
        piece.node.querySelector('[data-sm-piece-link]').href = productURL.href;
        fitLink.href = fitURL.href;
      }
      this.selectedPieces = this.pieces.filter(piece => piece.included);
      this.total.textContent = includedCount === 0 ? this.money.format(0) : allSelected && allAvailable ? this.money.format(total / 100) : 'Select your options';
      this.button.disabled = !(includedCount > 0 && allSelected && allAvailable);
      this.button.textContent = includedCount === 1 ? 'Add selected piece' : 'Add selected pieces';
      this.status.textContent = includedCount === 0 ? 'Choose at least one piece to continue.'
        : !allSelected ? 'Select the options for each included piece.'
        : !allAvailable ? 'Please review the availability of your selections.' : '';
    }

    async add(event) {
      event.preventDefault();
      if (this.busy || this.uncertain || this.button.disabled) return;
      this.update();
      if (this.button.disabled || !this.form.reportValidity()) return;
      const items = this.selectedPieces.map(piece => ({ id: piece.variant.id, quantity: 1 }));
      if (new Set(items.map(item => item.id)).size !== items.length) {
        this.status.textContent = 'Please open the pieces individually to complete your selection.';
        return;
      }
      this.busy = true;
      this.button.disabled = true;
      this.button.textContent = 'Adding your selection';
      this.form.setAttribute('aria-busy','true');
      this.pieces.forEach(piece => {
        piece.selects.forEach(select => { select.disabled = true; });
        if (piece.include) piece.include.disabled = true;
      });
      const controller = new AbortController();
      const timer = window.setTimeout(() => controller.abort(),20000);
      try {
        const addURL = new URL(this.dataset.cartAddUrl,window.location.origin);
        const cartURL = new URL(this.dataset.cartUrl,window.location.origin);
        if (addURL.origin !== window.location.origin || cartURL.origin !== window.location.origin) throw new Error('Invalid cart route');
        const response = await fetch(addURL.href, {
          method: 'POST', credentials: 'same-origin', signal: controller.signal,
          headers: {'Content-Type':'application/json','Accept':'application/json'},
          body: JSON.stringify({items})
        });
        if (!response.ok) throw new Error('Cart request was not confirmed');
        const result = await response.json();
        if (!Array.isArray(result.items) || !items.every(item => result.items.some(added => String(added.variant_id || added.id) === String(item.id)))) throw new Error('Incomplete cart response');
        this.status.textContent = 'Your selection has been added. Opening your cart.';
        this.cartLink.hidden = false;
        window.location.assign(cartURL.href);
      } catch (_) {
        // A timed-out or unsuccessful multi-item request may have changed the cart.
        // Never automatically retry and risk adding the same garments twice.
        this.uncertain = true;
        this.button.textContent = 'Please review your cart';
        this.status.textContent = 'We could not confirm the complete selection. Please review your cart before trying again.';
        this.cartLink.hidden = false;
        this.cartLink.focus();
      } finally {
        window.clearTimeout(timer);
        this.form.removeAttribute('aria-busy');
      }
    }
  }
  customElements.define('sm-complete-look',SMCompleteLook);
})();
